/**
 * AI Medical Scribe Component
 * Real-time transcription and medical notes generation
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Progress } from './ui/progress';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Mic, MicOff, Square, Play, Pause, Save, FileText,
  AlertCircle, CheckCircle, Loader2, Brain, Stethoscope,
  Pill, Calendar, ClipboardList, Download, RefreshCw, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MedicalScribe = ({ appointmentId, patientId, onNotesGenerated }) => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  
  // Transcription state
  const [transcription, setTranscription] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionSegments, setTranscriptionSegments] = useState([]);
  
  // Notes state
  const [medicalNotes, setMedicalNotes] = useState(null);
  const [isGeneratingNotes, setIsGeneratingNotes] = useState(false);
  const [activeTab, setActiveTab] = useState('recording');
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const animationRef = useRef(null);
  
  const headers = { Authorization: `Bearer ${token}` };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (timerRef.current) clearInterval(timerRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, []);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });
      
      // Setup audio analyser for visualization
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);
      
      // Start level monitoring
      const monitorLevel = () => {
        if (!analyserRef.current) return;
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(average / 255 * 100);
        animationRef.current = requestAnimationFrame(monitorLevel);
      };
      monitorLevel();
      
      // Setup MediaRecorder
      const options = { mimeType: 'audio/webm;codecs=opus' };
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        if (audioContextRef.current) audioContextRef.current.close();
        
        // Process recording
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await processRecording(audioBlob);
        }
      };
      
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success('Enregistrement démarré');
      
    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Veuillez autoriser l\'accès au microphone');
      } else {
        toast.error('Erreur lors du démarrage de l\'enregistrement');
      }
    }
  };

  // Pause/Resume recording
  const togglePause = () => {
    if (!mediaRecorderRef.current) return;
    
    if (isPaused) {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      mediaRecorderRef.current.pause();
      if (timerRef.current) clearInterval(timerRef.current);
    }
    setIsPaused(!isPaused);
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRecording(false);
    setIsPaused(false);
    setAudioLevel(0);
  };

  // Process recording - transcribe audio
  const processRecording = async (audioBlob) => {
    setIsTranscribing(true);
    setActiveTab('transcription');
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', 'fr');
      if (appointmentId) formData.append('appointment_id', appointmentId);
      
      const response = await axios.post(
        `${API_URL}/api/scribe/transcribe`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000 // 2 minutes timeout for long recordings
        }
      );
      
      if (response.data.text) {
        setTranscription(response.data.text);
        setTranscriptionSegments(response.data.segments || []);
        toast.success('Transcription terminée');
      } else if (response.data.error) {
        throw new Error(response.data.error);
      }
      
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Erreur lors de la transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Generate medical notes from transcription
  const generateNotes = async () => {
    if (!transcription) {
      toast.error('Aucune transcription disponible');
      return;
    }
    
    setIsGeneratingNotes(true);
    setActiveTab('notes');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/scribe/generate-notes`,
        {
          transcription,
          appointment_id: appointmentId,
          patient_id: patientId,
          consultation_type: 'general'
        },
        { headers }
      );
      
      if (response.data.notes) {
        setMedicalNotes(response.data.notes);
        toast.success('Notes médicales générées');
        
        if (onNotesGenerated) {
          onNotesGenerated(response.data.notes);
        }
      }
      
    } catch (error) {
      console.error('Notes generation error:', error);
      toast.error('Erreur lors de la génération des notes');
    } finally {
      setIsGeneratingNotes(false);
    }
  };

  // Save notes to database
  const saveNotes = async () => {
    if (!medicalNotes || !appointmentId) return;
    
    try {
      await axios.post(
        `${API_URL}/api/scribe/save-notes`,
        {
          appointment_id: appointmentId,
          transcription,
          medical_notes: medicalNotes
        },
        { headers }
      );
      
      toast.success('Notes sauvegardées');
      
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Export notes as PDF (placeholder)
  const exportPDF = () => {
    toast.info('Export PDF en cours d\'implémentation');
  };

  // Render prescriptions
  const renderPrescriptions = (prescriptions) => {
    if (!prescriptions || prescriptions.length === 0) return null;
    
    return (
      <div className="space-y-2">
        {prescriptions.map((rx, idx) => (
          <div 
            key={idx} 
            className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-blue-50'}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Pill className="w-4 h-4 text-blue-500" />
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {rx.medicament}
              </span>
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {rx.posologie} • {rx.duree}
            </p>
            {rx.instructions && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {rx.instructions}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className={`w-full max-w-4xl mx-auto ${isDarkMode ? 'bg-slate-900 border-slate-700' : ''}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
              <Brain className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <CardTitle className={isDarkMode ? 'text-white' : ''}>
                Scribe Médical IA
              </CardTitle>
              <CardDescription>
                Transcription et génération automatique de notes médicales
              </CardDescription>
            </div>
          </div>
          
          {medicalNotes && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportPDF}>
                <Download className="w-4 h-4 mr-1" />
                PDF
              </Button>
              <Button size="sm" onClick={saveNotes}>
                <Save className="w-4 h-4 mr-1" />
                Sauvegarder
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full grid grid-cols-3 mb-4">
            <TabsTrigger value="recording">
              <Mic className="w-4 h-4 mr-2" />
              Enregistrement
            </TabsTrigger>
            <TabsTrigger value="transcription" disabled={!transcription && !isTranscribing}>
              <FileText className="w-4 h-4 mr-2" />
              Transcription
            </TabsTrigger>
            <TabsTrigger value="notes" disabled={!medicalNotes && !isGeneratingNotes}>
              <ClipboardList className="w-4 h-4 mr-2" />
              Notes
            </TabsTrigger>
          </TabsList>

          {/* Recording Tab */}
          <TabsContent value="recording" className="space-y-6">
            <div className={`flex flex-col items-center justify-center py-8 rounded-xl ${
              isDarkMode ? 'bg-slate-800' : 'bg-gray-50'
            }`}>
              {/* Recording visualization */}
              <div className="relative mb-6">
                <div className={`w-32 h-32 rounded-full flex items-center justify-center ${
                  isRecording 
                    ? 'bg-red-500/20 animate-pulse' 
                    : isDarkMode ? 'bg-slate-700' : 'bg-gray-200'
                }`}>
                  {isRecording ? (
                    <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
                      isPaused ? 'bg-yellow-500' : 'bg-red-500'
                    }`}>
                      <Mic className="w-10 h-10 text-white" />
                    </div>
                  ) : (
                    <Mic className={`w-10 h-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  )}
                </div>
                
                {/* Audio level indicator */}
                {isRecording && (
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-full">
                    <Progress value={audioLevel} className="h-2" />
                  </div>
                )}
              </div>

              {/* Timer */}
              <div className={`text-3xl font-mono mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {formatTime(recordingTime)}
              </div>

              {/* Controls */}
              <div className="flex items-center gap-4">
                {!isRecording ? (
                  <Button 
                    size="lg" 
                    onClick={startRecording}
                    className="rounded-full px-8"
                  >
                    <Mic className="w-5 h-5 mr-2" />
                    Démarrer
                  </Button>
                ) : (
                  <>
                    <Button
                      size="lg"
                      variant={isPaused ? 'default' : 'outline'}
                      onClick={togglePause}
                      className="rounded-full"
                    >
                      {isPaused ? (
                        <>
                          <Play className="w-5 h-5 mr-2" />
                          Reprendre
                        </>
                      ) : (
                        <>
                          <Pause className="w-5 h-5 mr-2" />
                          Pause
                        </>
                      )}
                    </Button>
                    
                    <Button
                      size="lg"
                      variant="destructive"
                      onClick={stopRecording}
                      className="rounded-full"
                    >
                      <Square className="w-5 h-5 mr-2" />
                      Arrêter
                    </Button>
                  </>
                )}
              </div>

              {/* Recording tips */}
              {!isRecording && (
                <div className={`mt-8 text-sm text-center max-w-md ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <p className="mb-2">
                    Conseils pour une meilleure transcription :
                  </p>
                  <ul className="text-left list-disc list-inside space-y-1">
                    <li>Parlez clairement près du microphone</li>
                    <li>Évitez les bruits de fond</li>
                    <li>Identifiez clairement les symptômes et diagnostics</li>
                  </ul>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Transcription Tab */}
          <TabsContent value="transcription" className="space-y-4">
            {isTranscribing ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                <p className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                  Transcription en cours...
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Cela peut prendre quelques instants
                </p>
              </div>
            ) : transcription ? (
              <>
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTime(recordingTime)} d'enregistrement
                  </Badge>
                  
                  <Button onClick={generateNotes} disabled={isGeneratingNotes}>
                    <Brain className="w-4 h-4 mr-2" />
                    Générer les notes
                  </Button>
                </div>
                
                <ScrollArea className={`h-[400px] rounded-lg p-4 ${
                  isDarkMode ? 'bg-slate-800' : 'bg-gray-50'
                }`}>
                  {transcriptionSegments.length > 0 ? (
                    <div className="space-y-3">
                      {transcriptionSegments.map((segment, idx) => (
                        <div key={idx} className="flex gap-3">
                          <span className={`text-xs font-mono ${
                            isDarkMode ? 'text-gray-500' : 'text-gray-400'
                          }`}>
                            {formatTime(Math.floor(segment.start))}
                          </span>
                          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                            {segment.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`whitespace-pre-wrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {transcription}
                    </p>
                  )}
                </ScrollArea>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <FileText className={`w-12 h-12 mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Aucune transcription
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Enregistrez d'abord une consultation
                </p>
              </div>
            )}
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="space-y-4">
            {isGeneratingNotes ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-12 h-12 text-purple-500 animate-spin mb-4" />
                <p className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>
                  Génération des notes médicales...
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  L'IA analyse la transcription
                </p>
              </div>
            ) : medicalNotes ? (
              <ScrollArea className="h-[500px]">
                <div className="space-y-6">
                  {/* Résumé */}
                  {medicalNotes.resume && (
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-green-50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Résumé
                        </h3>
                      </div>
                      <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                        {medicalNotes.resume}
                      </p>
                    </div>
                  )}

                  {/* Motif de consultation */}
                  {medicalNotes.motif_consultation && (
                    <div>
                      <h3 className={`font-semibold mb-2 flex items-center gap-2 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        <Stethoscope className="w-4 h-4 text-blue-500" />
                        Motif de consultation
                      </h3>
                      <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                        {medicalNotes.motif_consultation}
                      </p>
                    </div>
                  )}

                  <Separator />

                  {/* Symptômes */}
                  {medicalNotes.symptomes?.length > 0 && (
                    <div>
                      <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Symptômes
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {medicalNotes.symptomes.map((symptom, idx) => (
                          <Badge key={idx} variant="secondary">
                            {symptom}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Diagnostic */}
                  {medicalNotes.diagnostic && (
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-yellow-50'}`}>
                      <h3 className={`font-semibold mb-2 flex items-center gap-2 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                        Diagnostic
                      </h3>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {medicalNotes.diagnostic.principal}
                      </p>
                      {medicalNotes.diagnostic.differentiels?.length > 0 && (
                        <div className="mt-2">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Diagnostics différentiels:
                          </span>
                          <ul className="list-disc list-inside mt-1">
                            {medicalNotes.diagnostic.differentiels.map((d, idx) => (
                              <li key={idx} className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {d}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Prescriptions */}
                  {medicalNotes.traitement?.prescriptions?.length > 0 && (
                    <div>
                      <h3 className={`font-semibold mb-3 flex items-center gap-2 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        <Pill className="w-4 h-4 text-blue-500" />
                        Prescriptions
                      </h3>
                      {renderPrescriptions(medicalNotes.traitement.prescriptions)}
                    </div>
                  )}

                  {/* Suivi */}
                  {medicalNotes.suivi && (
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-purple-50'}`}>
                      <h3 className={`font-semibold mb-2 flex items-center gap-2 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        <Calendar className="w-4 h-4 text-purple-500" />
                        Suivi
                      </h3>
                      {medicalNotes.suivi.prochain_rdv && (
                        <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                          Prochain RDV: {medicalNotes.suivi.prochain_rdv}
                        </p>
                      )}
                      {medicalNotes.suivi.signes_alerte?.length > 0 && (
                        <div className="mt-2">
                          <span className={`text-sm font-medium text-red-500`}>
                            Signes d'alerte à surveiller:
                          </span>
                          <ul className="list-disc list-inside mt-1">
                            {medicalNotes.suivi.signes_alerte.map((s, idx) => (
                              <li key={idx} className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Résumé patient */}
                  {medicalNotes.resume_patient && (
                    <div className={`p-4 rounded-lg border-2 border-dashed ${
                      isDarkMode ? 'border-slate-600 bg-slate-800/50' : 'border-gray-200 bg-gray-50'
                    }`}>
                      <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Résumé pour le patient
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {medicalNotes.resume_patient}
                      </p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <ClipboardList className={`w-12 h-12 mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Aucune note générée
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Transcrivez d'abord une consultation
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default MedicalScribe;
